import React, { useEffect, useState, useCallback, useRef } from "react";
import ReactDOM from "react-dom/client";
import { useNavigate } from "react-router-dom";
import { Sidebar } from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/Header";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { endpoints } from "@/config/apiEndpoints";
import axiosInstance from "@/config/axiosInstance";
import Pricing from "@/pages/Pricing";
import { Loader2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";

import {
  ArrowRight,
  ArrowLeft,
  Briefcase,
  Upload,
  Send,
  CheckCircle,
  RefreshCw,
  Search,
  Layout,
  Copy,
  Star,
} from "lucide-react";

import { Dialog, DialogContent } from "@/components/ui/dialog";

interface ServiceCampaignData {
  serviceType: string;
  description: string;
  headline: string;
  heroImage?: string;
  showCost?: boolean;
  cost?: string;
  businessName?: string;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
}

interface Template {
  id: number;
  name: string;
  category: string;
  style: string;
  rating: number;
  uses: number;
  tag: string;
  description: string;
  component: React.ComponentType<any>;
}

// Reuse same templates (they can render service data too)
import Template1 from "@/components/templates/Template1";
import Template2 from "@/components/templates/Template2";
import Template3 from "@/components/templates/Template3";
import Template4 from "@/components/templates/Template4";
import Template5 from "@/components/templates/Template5";
import Template6 from "@/components/templates/Template6";
import Template7 from "@/components/templates/Template7";
import Template8 from "@/components/templates/Template8";
import { sanitizeHtml } from "@/utils/sanitizeHtml";

const templates: Template[] = [
  {
    id: 1,
    name: "Template 1",
    category: "Modern Hero Layout",
    style: "Clean & Professional",
    rating: 4.8,
    uses: 1247,
    tag: "Promotion",
    description: "",
    component: Template1,
  },
  {
    id: 2,
    name: "Template 2",
    category: "Luxury Showcase",
    style: "Premium & Elegant",
    rating: 4.9,
    uses: 982,
    tag: "Special Offer",
    description: "",
    component: Template2,
  },
  {
    id: 3,
    name: "Template 3",
    category: "Gallery Focus",
    style: "Image-Driven",
    rating: 4.7,
    uses: 756,
    tag: "New Service",
    description: "",
    component: Template3,
  },
  {
    id: 4,
    name: "Template 4",
    category: "Traditional Layout",
    style: "Classic & Trustworthy",
    rating: 4.6,
    uses: 634,
    tag: "Limited Time",
    description: "",
    component: Template4,
  },
  {
    id: 5,
    name: "Template 5",
    category: "Split Screen",
    style: "Modern Balance",
    rating: 4.8,
    uses: 523,
    tag: "Promotion",
    description: "",
    component: Template5,
  },
  {
    id: 6,
    name: "Template 6",
    category: "Feature-Rich",
    style: "Comprehensive Info",
    rating: 4.9,
    uses: 445,
    tag: "Special Offer",
    description: "",
    component: Template6,
  },
  {
    id: 7,
    name: "Template 7",
    category: "Bold Impact",
    style: "Eye-Catching",
    rating: 4.7,
    uses: 378,
    tag: "New Service",
    description: "",
    component: Template7,
  },
  {
    id: 8,
    name: "Template 8",
    category: "Minimalist Pro",
    style: "Simple & Effective",
    rating: 4.8,
    uses: 298,
    tag: "Limited Time",
    description: "",
    component: Template8,
  },
];

const filters = [
  { id: "all", label: "All Templates" },
  { id: "modern", label: "Modern" },
  { id: "luxury", label: "Luxury" },
  { id: "traditional", label: "Traditional" },
  { id: "gallery", label: "Gallery Focus" },
];

const serviceTypes = [
  "Plumbing",
  "Electrician",
  "Landscaping",
  "Cleaning",
  "HVAC",
  "Pest Control",
  "Roofing",
  "Home Repair",
];

const ServiceCampaignBuilder = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [currentStep, setCurrentStep] = useState(1);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(
    null
  );
  const [customTemplates, setCustomTemplates] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [selectedDatasets, setSelectedDatasets] = useState<string[]>([]);
  const [selectedState, setSelectedState] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [selectedDateTime, setSelectedDateTime] = useState("");
  const [subject, setSubject] = useState("");
  const [error, setError] = useState("");

  const [statesList, setStatesList] = useState<
    { id: number; state_name: string }[]
  >([]);
  const [agentLists, setAgentLists] = useState<
    { id: string; name: string; count: number }[]
  >([]);

  const [serviceData, setServiceData] = useState<ServiceCampaignData>({
    serviceType: "",
    description: "",
    headline: "",
    showCost: false,
    cost: "",
    businessName: "",
    contactName: "",
    contactPhone: "",
    contactEmail: "",
  });

  const [generationCount, setGenerationCount] = useState(0);
  const [isRegenerating, setIsRegenerating] = useState(false);

  const [planLoading, setPlanLoading] = useState(false);
  const [countLoading, setCountLoading] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [userPlan, setUserPlan] = useState<string | null>(null);
  const [campaignsSent, setCampaignsSent] = useState<number>(0);
  const [planLimits, setPlanLimits] = useState<Record<string, number>>({});

  const maxAllowed = planLimits[userPlan || ""] ?? 0;

  // Load plan limits
  useEffect(() => {
    const fetchPlanLimits = async () => {
      try {
        const { data } = await axiosInstance.get(endpoints.plans);
        const limits: Record<string, number> = {};
        data.forEach((p: any) => {
          limits[p.name] = p.Campaigns ?? 0;
        });
        setPlanLimits(limits);
      } catch (err) {
        console.error("Failed to load plan limits", err);
      }
    };
    fetchPlanLimits();
  }, []);

  // Load states
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const res = await axiosInstance.get(endpoints.db_states);
        setStatesList(res.data);
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to load states",
          variant: "destructive",
        });
      }
    };
    fetchStates();
  }, [toast]);

  // Load saved custom templates from backend
  useEffect(() => {
    const fetchCustomTemplates = async () => {
      try {
        const res = await axiosInstance.get(endpoints.custom_templates);
        setCustomTemplates(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error('Failed to load custom templates', err);
      }
    };
    fetchCustomTemplates();
  }, []);

  // Load agent lists by state
  useEffect(() => {
    if (!selectedState) {
      setAgentLists([]);
      return;
    }
    const fetchAgentLists = async () => {
      try {
        const res = await axiosInstance.get(
          `${endpoints.db_cities}?state=${selectedState.id}`
        );
        const data = (
          Array.isArray(res.data) ? res.data : res.data.results || []
        ).map((city: any) => ({
          id: String(city.id || city.pk || Math.random()),
          name: city.city_name || city.name || "Unknown",
          count: city.agent_count || city.agents?.length || 0,
        }));
        setAgentLists(data);
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to load agent lists",
          variant: "destructive",
        });
      }
    };
    fetchAgentLists();
  }, [selectedState, toast]);

  const filteredTemplates = templates.filter(
    (t) =>
      (t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.style.toLowerCase().includes(searchQuery.toLowerCase())) &&
      (activeFilter === "all" ||
        t.category.toLowerCase().includes(activeFilter))
  );

  const handleBack = () => setCurrentStep((prev) => Math.max(1, prev - 1));

  /**
   * Capture rendered React component as HTML with inline styles.
   * This is used for file-based templates (template1, template2, etc.)
   */
  const captureTemplateHTML = async (template: Template): Promise<string | null> => {
    try {
      // Create temporary container
      const container = document.createElement("div");
      container.style.position = "fixed";
      container.style.left = "-9999px";
      container.style.top = "-9999px";
      container.style.width = "600px"; // Email standard width
      container.style.backgroundColor = "white";
      document.body.appendChild(container);

      // Render component into container using React
      const root = ReactDOM.createRoot(container);
      const TemplateComponent = template.component;
      const templateData = {
        heroImage: serviceData.heroImage || "",
        headline: serviceData.headline,
        description: serviceData.description,
        businessName: serviceData.businessName,
        contactName: serviceData.contactName,
        contactPhone: serviceData.contactPhone,
        contactEmail: serviceData.contactEmail,
        cost: serviceData.showCost ? serviceData.cost : undefined,
        serviceType: serviceData.serviceType,
      };

      // Render the component
      root.render(<TemplateComponent data={templateData} />);

      // Wait for React to render
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Get the HTML with all computed styles inlined
      const html = container.innerHTML;

      // Extract all stylesheets and computed styles
      const styleElements = document.querySelectorAll("style");
      let embeddedStyles = "";
      styleElements.forEach((styleEl) => {
        embeddedStyles += styleEl.textContent + "\n";
      });

      // Extract CSS from the rendered component's computed styles
      const computedCSSParts: string[] = [];
      const allElements = container.querySelectorAll("*");
      allElements.forEach((el: any) => {
        const styles = window.getComputedStyle(el);
        if (styles.cssText) {
          const selector = el.className
            ? `.${el.className.split(" ").join(".")}`
            : el.tagName.toLowerCase();
          computedCSSParts.push(`${selector} { ${styles.cssText} }`);
        }
      });

      // Combine into final HTML with embedded styles
      const finalHTML =
        embeddedStyles + (computedCSSParts.length ? `<style>${embeddedStyles}</style>` : "") + html;

      // Cleanup
      root.unmount();
      document.body.removeChild(container);

      // Store in sessionStorage for later use in payload
      sessionStorage.setItem("campaign_template_html", finalHTML);

      return finalHTML;
    } catch (error) {
      console.error("Error capturing template HTML:", error);
      return null;
    }
  };

  const handleTemplateSelect = async (template: Template) => {
    // Capture full HTML with CSS from React component
    const html = await captureTemplateHTML(template);
    setSelectedTemplate({ ...template, html } as any);
    setCurrentStep(4);
  };

  const handleTemplateSelectCustom = (ct: any) => {
    // ct is the object from backend: { id, name, html, ... }
    setSelectedTemplate({ id: ct.id, name: ct.name || `Custom ${ct.id}`, is_custom: true, html: ct.html } as any);
    setCurrentStep(4);
  };

  const handleAddDataset = (id: string) =>
    setSelectedDatasets((prev) => [...prev.filter((x) => x !== id), id]);
  const handleRemoveDataset = (id: string) =>
    setSelectedDatasets((prev) => prev.filter((x) => x !== id));

  const handleRegenerateSubject = async () => {
    if (isRegenerating || generationCount >= 3) return;

    setIsRegenerating(true);
    const prompt = `
You are a service marketing expert. Generate 3 short, catchy email subject lines (max 50 chars) for a "${
      serviceData.serviceType
    }" service promotion.

Business: ${serviceData.businessName || "Local Business"}
Offer: ${serviceData.headline || "Special Promotion"}
Description: ${serviceData.description.slice(0, 80)}...

Focus on urgency, savings, or trust. Output only the 3 lines, one per line.
    `.trim();

    try {
      const response = await fetch(
        "https://api.openai.com/v1/chat/completions",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env.VITE_OPENAI_API_KEY}`,
          },
          body: JSON.stringify({
            model: "gpt-3.5-turbo",
            messages: [{ role: "user", content: prompt }],
            max_tokens: 30,
            temperature: 0.8,
          }),
        }
      );

      const data = await response.json();
      const lines = (data.choices?.[0]?.message?.content || "")
        .split("\n")
        .map((l: string) => l.trim())
        .filter((l: string) => l && l.length <= 50);

      if (lines.length > 0) {
        setSubject(lines[0]);
        setGenerationCount((c) => c + 1);
        setError("");
      }
    } catch (err: any) {
      toast({
        title: "Failed",
        description: err.message || "AI generation failed",
        variant: "destructive",
      });
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleSendCampaign = async () => {
    if (!subject.trim()) {
      setError("Subject is required");
      toast({
        title: "Missing Subject",
        description: "Please enter a subject.",
        variant: "destructive",
      });
      return;
    }
    if (!selectedDateTime) {
      toast({
        title: "Missing Schedule",
        description: "Select date and time.",
        variant: "destructive",
      });
      return;
    }
    if (selectedDatasets.length === 0) {
      toast({
        title: "No Dataset",
        description: "Select at least one agent database.",
        variant: "destructive",
      });
      return;
    }

    setPlanLoading(true);
    try {
      const { data } = await axiosInstance.get(endpoints.me);
      const activePlan = data?.plan_name ?? null;
      if (!activePlan) {
        setShowPricingModal(true);
        return;
      }
      setUserPlan(activePlan);

      setCountLoading(true);
      const { data: countData } = await axiosInstance.get(
        `${endpoints.campaign_counts}`
      );
      const sent = countData.sent ?? 0;
      setCampaignsSent(sent);
      setCountLoading(false);

      const limit = planLimits[activePlan] ?? 0;
      if (sent >= limit && limit !== Infinity) {
        toast({
          title: "Limit Reached",
          description: `Your ${activePlan} plan allows ${limit} launches. Upgrade to send more.`,
          variant: "destructive",
        });
        setShowPricingModal(true);
        return;
      }

      let htmlSnapshot =
        (selectedTemplate as any)?.html ||
        sessionStorage.getItem("campaign_template_html") ||
        null;

      if (selectedTemplate && !(selectedTemplate as any)?.is_custom) {
        const freshHtml = await captureTemplateHTML(selectedTemplate);
        if (freshHtml) {
          htmlSnapshot = freshHtml;
          setSelectedTemplate({ ...selectedTemplate, html: freshHtml } as any);
        }
      }

      const payload: any = {
        campaign_type: "SERVICE",
        campaign_subject: subject.trim(),
        property_id: null,
        state_id: selectedState?.id || null,
        city_id: selectedDatasets.map(Number),
        // send backend template key (e.g. "template6") so backend maps file templates correctly
        template: selectedTemplate ? `template${(selectedTemplate as any).id}` : "",
        // Only send a custom_template_id when the selected template is a saved custom template
        custom_template_id: (selectedTemplate as any)?.is_custom ? (selectedTemplate as any)?.id ?? null : null,
        scheduled_date: selectedDateTime,
        service_data: serviceData,
      };

      if (htmlSnapshot) payload.html = htmlSnapshot;

      await axiosInstance.post(endpoints.createCampaign, payload);

      toast({
        title: "Launch Scheduled",
        description: `Will run on ${new Date(
          selectedDateTime
        ).toLocaleString()}.`,
        variant: "success",
      });

      setTimeout(() => navigate("/launches"), 2000);
    } catch (error: any) {
      toast({
        title: "Error",
        description:
          error.response?.data?.message || "Failed to schedule launch.",
        variant: "destructive",
      });
    } finally {
      setPlanLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="text-center max-w-2xl mx-auto">
            <Briefcase className="h-16 w-16 text-primary mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-primary mb-4">
              Service Launch Details
            </h2>
            <div className="space-y-6">
              <div>
                <Label>Service Type</Label>
                <Select
                  value={serviceData.serviceType}
                  onValueChange={(v) =>
                    setServiceData({ ...serviceData, serviceType: v })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select service" />
                  </SelectTrigger>
                  <SelectContent>
                    {serviceTypes.map((t) => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* <div>
                <Label>Business Name</Label>
                <Input
                  placeholder="e.g., ProClean Services"
                  value={serviceData.businessName}
                  onChange={(e) => setServiceData({ ...serviceData, businessName: e.target.value })}
                />
              </div>

              <div>
                <Label>Headline / Offer</Label>
                <Input
                  placeholder="e.g., 20% Off First Cleaning!"
                  value={serviceData.headline}
                  onChange={(e) => setServiceData({ ...serviceData, headline: e.target.value })}
                />
              </div> */}

              <div>
                <Label>Description</Label>
                <Textarea
                  placeholder="Tell agents about your service..."
                  rows={4}
                  value={serviceData.description}
                  onChange={(e) =>
                    setServiceData({
                      ...serviceData,
                      description: e.target.value,
                    })
                  }
                />
              </div>

              {/* <div className="flex items-center space-x-2">
                <Switch
                  checked={serviceData.showCost}
                  onCheckedChange={(v) => setServiceData({ ...serviceData, showCost: v })}
                />
                <Label>Show Pricing</Label>
              </div> */}

              {serviceData.showCost && (
                <div>
                  <Label>Starting Price</Label>
                  <Input
                    placeholder="$99"
                    value={serviceData.cost}
                    onChange={(e) =>
                      setServiceData({ ...serviceData, cost: e.target.value })
                    }
                  />
                </div>
              )}

              <div className="flex justify-end">
                <Button
                  onClick={() => setCurrentStep(2)}
                  disabled={!serviceData.serviceType || !serviceData.headline}
                >
                  Next <ArrowRight className="ml-2" />
                </Button>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="max-w-7xl mx-auto">
            <div className="mb-8">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h1 className="text-3xl font-bold text-primary">
                    Service Email Templates
                  </h1>
                  <p className="text-muted-foreground mt-1">
                    Choose a template to promote your {serviceData.serviceType}{" "}
                    service.
                  </p>
                </div>
                <Button
                  variant="cta"
                  size="lg"
                  onClick={() => navigate("/template-builder")}
                >
                  <Layout className="mr-2 h-4 w-4" />
                  Create Custom Template
                </Button>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 items-center">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
                  <Input
                    placeholder="Search templates..."
                    className="pl-10"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <div className="flex flex-wrap gap-2">
                  {filters.map((filter) => (
                    <Button
                      key={filter.id}
                      variant={activeFilter === filter.id ? "cta" : "outline"}
                      size="sm"
                      onClick={() => setActiveFilter(filter.id)}
                    >
                      {filter.label}
                    </Button>
                  ))}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {filteredTemplates.map((template) => {
                const TemplateComponent = template.component;
                return (
                  <Card
                    key={template.id}
                    className="group cursor-pointer hover:shadow-elevated bg-gradient-card"
                    onClick={() => handleTemplateSelect(template)}
                  >
                    <CardContent className="p-0">
                      <div className="relative overflow-hidden rounded-t-lg">
                        <div className="w-full h-48 bg-white relative overflow-hidden">
                          <div className="scale-[0.25] origin-top-left w-[400%] h-[400%] pointer-events-none">
                            <TemplateComponent
                              data={{
                                heroImage: serviceData.heroImage || "",
                                headline: serviceData.headline,
                                description: serviceData.description,
                                businessName: serviceData.businessName,
                                contactName: serviceData.contactName,
                                contactPhone: serviceData.contactPhone,
                                contactEmail: serviceData.contactEmail,
                                cost: serviceData.showCost
                                  ? serviceData.cost
                                  : undefined,
                                serviceType: serviceData.serviceType,
                              }}
                            />
                          </div>
                        </div>
                        <div className="absolute inset-0 bg-primary/20 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                          <Button size="sm" variant="professional">
                            <Copy className="mr-2 h-4 w-4" />
                            Use Template
                          </Button>
                        </div>
                      </div>
                      <div className="p-4">
                        <div className="flex justify-between items-start mb-2">
                          <h3 className="font-semibold text-primary">
                            {template.name}
                          </h3>
                          <div className="flex items-center">
                            <Star className="h-4 w-4 text-yellow-400 mr-1" />
                            <span className="text-sm text-muted-foreground">
                              {template.rating}
                            </span>
                          </div>
                        </div>
                        <div className="flex justify-between items-center">
                          <div className="flex space-x-2">
                            <Badge variant="secondary">
                              {template.category}
                            </Badge>
                            <Badge variant="outline">{template.style}</Badge>
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {template.uses.toLocaleString()} uses
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
              {/* Saved custom templates from backend */}
              {customTemplates.map((ct) => (
                <Card
                  key={`custom-${ct.id}`}
                  className="group cursor-pointer hover:shadow-elevated bg-gradient-card"
                  onClick={() => handleTemplateSelectCustom(ct)}
                >
                  <CardContent className="p-0">
                    <div className="relative overflow-hidden rounded-t-lg">
                      <div className="w-full h-48 bg-white relative overflow-hidden">
                        <div className="p-2 w-full h-full overflow-hidden">
                          <div
                            className="w-full h-full overflow-hidden text-sm"
                            // preview the saved HTML (safe enough for admin/author previews)
                            dangerouslySetInnerHTML={{
                              __html: sanitizeHtml(ct.html || '<div style="padding:16px">No preview</div>')
                            }}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="font-semibold text-primary">{ct.name}</h3>
                        <div className="flex items-center">
                          <Star className="h-4 w-4 text-yellow-400 mr-1" />
                        </div>
                      </div>
                      <div className="flex justify-between items-center">
                        <div className="flex space-x-2">
                          <Badge variant="secondary">Custom</Badge>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="flex justify-start">
              <Button variant="outline" onClick={handleBack}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-bold text-primary mb-6">
              Select Dataset
            </h2>

            <div className="mb-8 flex items-center justify-center gap-4">
              <Label className="text-lg font-semibold text-primary">
                Select State
              </Label>
              <Select
                value={selectedState?.id?.toString()}
                onValueChange={(value) => {
                  const s = statesList.find((st) => st.id.toString() === value);
                  if (s) setSelectedState({ id: s.id, name: s.state_name });
                }}
              >
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Select State" />
                </SelectTrigger>
                <SelectContent>
                  {statesList.map((state) => (
                    <SelectItem key={state.id} value={state.id.toString()}>
                      {state.state_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-4">
              {agentLists.map((list) => (
                <div
                  key={list.id}
                  className={`border rounded-lg p-4 transition-all ${
                    selectedDatasets.includes(list.id)
                      ? "bg-primary/10 border-primary shadow-lg"
                      : "bg-background border-border hover:shadow-card"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-primary font-medium">
                        {list.name} -{" "}
                        <strong>{(list.agent_count ?? list.count ?? 0).toLocaleString()}</strong>
                      </span>
                    </div>
                    {selectedDatasets.includes(list.id) ? (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleRemoveDataset(list.id)}
                      >
                        Remove
                      </Button>
                    ) : (
                      <Button
                        variant="cta"
                        size="sm"
                        onClick={() => handleAddDataset(list.id)}
                      >
                        Add
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-between pt-8">
              <Button variant="outline" onClick={handleBack}>
                <ArrowLeft className="mr-2" />
                Back
              </Button>
              <Button
                onClick={() => setCurrentStep(4)}
                disabled={selectedDatasets.length === 0}
              >
                Continue <ArrowRight className="ml-2" />
              </Button>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <h2 className="text-3xl font-bold text-primary">
                Service Launch Ready!
              </h2>
            </div>

            <Card className="border-2 border-primary/20">
              <CardHeader>
                <CardTitle className="text-primary">Launch Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-sm text-muted-foreground">
                    Service
                  </Label>
                  <p className="text-primary font-semibold">
                    {serviceData.serviceType}
                  </p>
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">
                    Headline
                  </Label>
                  <p className="text-primary font-semibold">
                    {serviceData.headline}
                  </p>
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">
                    Template
                  </Label>
                  <p className="text-primary font-semibold">
                    {selectedTemplate?.name}
                  </p>
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">State</Label>
                  <p className="text-primary font-semibold">
                    {selectedState?.name}
                  </p>
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">
                    Databases
                  </Label>
                  <p className="text-primary font-semibold">
                    {selectedDatasets.length} selected
                  </p>
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">
                    Schedule
                  </Label>
                  <input
                    type="datetime-local"
                    value={selectedDateTime}
                    onChange={(e) => setSelectedDateTime(e.target.value)}
                    min={new Date().toISOString().slice(0, 16)}
                    className="border rounded px-3 py-2"
                  />
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">
                    Subject
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      value={subject}
                      onChange={(e) => {
                        setSubject(e.target.value);
                        if (error) setError("");
                      }}
                      placeholder="e.g., 20% Off Plumbing This Week!"
                      className={error ? "border-red-500" : ""}
                    />
                    <Button
                      variant="outline"
                      onClick={handleRegenerateSubject}
                      disabled={isRegenerating || generationCount >= 3}
                    >
                      <RefreshCw
                        className={`h-4 w-4 mr-2 ${
                          isRegenerating ? "animate-spin" : ""
                        }`}
                      />
                      {generationCount === 0
                        ? "Generate"
                        : generationCount < 3
                        ? "Regenerate"
                        : "Limit"}
                    </Button>
                  </div>
                  {error && (
                    <p className="text-red-500 text-sm mt-1">{error}</p>
                  )}
                  {generationCount > 0 && generationCount < 3 && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {3 - generationCount} left
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-between mt-8">
              <Button variant="outline" onClick={handleBack}>
                <ArrowLeft className="mr-2" />
                Back
              </Button>
              <Button
                onClick={handleSendCampaign}
                disabled={
                  planLoading || countLoading || selectedDatasets.length === 0
                }
                className="bg-green-600 hover:bg-green-700"
              >
                {planLoading || countLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Checking...
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    Send Launch
                  </>
                )}
              </Button>
            </div>

            <Dialog open={showPricingModal} onOpenChange={setShowPricingModal}>
              <DialogContent className="max-w-[98vw] w-[80vw] h-[90vh] overflow-y-auto rounded-2xl p-0">
                <Pricing />
              </DialogContent>
            </Dialog>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />
      <main className="flex-1 flex flex-col">
        <PageHeader
          title="Service Launch Wizard"
          subtitle="Promote your business services"
        />
        <div className="max-w-7xl mx-auto px-4 w-full">
          <div className="flex justify-center mb-8 mt-8">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center text-base font-medium transition-all ${
                    currentStep >= i
                      ? "bg-primary text-white"
                      : "bg-muted text-muted-foreground"
                  }`}
                >
                  {i}
                </div>
                {i < 4 && (
                  <div
                    className={`w-16 h-0.5 mx-3 transition-colors ${
                      currentStep > i ? "bg-primary" : "bg-muted"
                    }`}
                  />
                )}
              </div>
            ))}
          </div>

          <Card className="shadow-elevated border-0">
            <CardContent className="p-6 lg:p-10">
              {renderStepContent()}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default ServiceCampaignBuilder;
